import { useMemo, useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  PsvProviderStatus,
  PsvReviewItemKind,
} from "@api/shared-runtime/psv-review";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getReportIdentity } from "@/features/credentials/components/primary-source-runs/run-report-dialog/report-identity";
import { ACTIVE_RUN_POLL_MS } from "@/features/credentials/components/primary-source-runs/types";
import { useTRPC } from "@/trpc";
import { ProviderStartVerification } from "../provider-start-verification";
import { readLastWorkspaceSearch } from "../workspace-last-search";
import { useWorkspaceRefresh } from "../use-workspace-refresh";
import type { WorkspaceProvider } from "../workspace-types";
import { CredentialVaultTab } from "@/features/credentials/components/credential-vault-view";
import { CredentialRecord } from "./documents/credential-record";
import {
  ProviderPageHeader,
  type ProviderRunFacts,
} from "./provider-page-header";
import {
  ProviderPageTab,
  type ProviderPageSearch,
} from "./provider-page-search";
import { useProviderVerification } from "./use-provider-verification";
import { VerificationTab } from "./verification/verification-tab";

const NO_CREDENTIAL_ACCESS =
  "You do not have permission to view this provider’s credentials.";
const GRID_ROUTE = "/org/bu/$organizationBusinessUnitId/providers/credentials";
const TAB_LABELS: Record<ProviderPageTab, string> = {
  [ProviderPageTab.Verification]: "Verification",
  [ProviderPageTab.Credentials]: "Credentials",
  [ProviderPageTab.Documents]: "Documents",
};

/** The credential vault view each record tab shows. */
const RECORD_VIEWS: Partial<Record<ProviderPageTab, CredentialVaultTab>> = {
  [ProviderPageTab.Credentials]: CredentialVaultTab.Credentials,
  [ProviderPageTab.Documents]: CredentialVaultTab.Files,
};

/** The tab to show: the requested one when this provider has it, otherwise
 * Verification when available, then Credentials. Legacy records list files
 * under each credential, so they have no Documents tab. */
function resolveTab({
  requested,
  verificationEnabled,
  hasDocumentsTab,
}: {
  requested?: ProviderPageTab;
  verificationEnabled: boolean;
  hasDocumentsTab: boolean;
}): ProviderPageTab {
  const fallback = verificationEnabled
    ? ProviderPageTab.Verification
    : ProviderPageTab.Credentials;
  if (requested === ProviderPageTab.Verification && !verificationEnabled) {
    return fallback;
  }
  if (requested === ProviderPageTab.Documents && !hasDocumentsTab) {
    return fallback;
  }
  return requested ?? fallback;
}
const TAB_TRIGGER_CLASS =
  "rounded-none border-b-2 border-transparent px-4 py-3 text-base data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:shadow-none";

export function ProviderPage({
  organizationBusinessUnitId,
  affiliationId,
  businessUnitName,
  search,
  onSearchChange,
}: {
  organizationBusinessUnitId: string;
  affiliationId: string;
  businessUnitName?: string;
  search: ProviderPageSearch;
  onSearchChange: (changes: Partial<ProviderPageSearch>) => void;
}) {
  const trpc = useTRPC();
  useWorkspaceRefresh(organizationBusinessUnitId);
  const query = useQuery({
    ...trpc.providerWorkspace.getProvider.queryOptions({
      organizationBusinessUnitId,
      affiliationId,
    }),
    staleTime: 0,
    refetchInterval: (current) =>
      current.state.data?.psvStatus?.status === PsvProviderStatus.InProgress
        ? ACTIVE_RUN_POLL_MS
        : false,
  });
  if (query.isError) {
    return (
      <PageShell>
        <div role="alert" className="space-y-3">
          <p>{query.error.message}</p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => void query.refetch()}>
              Try again
            </Button>
            <Button variant="ghost" asChild>
              <Link
                to={GRID_ROUTE}
                params={{ organizationBusinessUnitId }}
                search={readLastWorkspaceSearch(organizationBusinessUnitId)}
              >
                Back to provider credentials
              </Link>
            </Button>
          </div>
        </div>
      </PageShell>
    );
  }
  if (!query.data) {
    return (
      <PageShell>
        <div role="status" aria-busy="true" className="space-y-4">
          <span className="sr-only">Loading provider…</span>
          <Skeleton aria-hidden className="h-8 w-64" />
          <Skeleton aria-hidden className="h-5 w-48" />
          <Skeleton aria-hidden className="h-64 rounded-xl" />
        </div>
      </PageShell>
    );
  }
  return (
    <ProviderPageBody
      key={`${organizationBusinessUnitId}:${affiliationId}`}
      provider={query.data}
      organizationBusinessUnitId={organizationBusinessUnitId}
      businessUnitName={businessUnitName}
      search={search}
      onSearchChange={onSearchChange}
    />
  );
}

function PageShell({ children }: { children: ReactNode }) {
  return <div className="space-y-6 p-4 md:p-6">{children}</div>;
}

function licensedStates(items: { kind: string; stateCode: string | null }[]) {
  const states = items
    .filter((item) => item.kind === PsvReviewItemKind.StateLicense)
    .map((item) => item.stateCode)
    .filter((code): code is string => !!code);
  return [...new Set(states)].sort();
}

function ProviderPageBody({
  provider,
  organizationBusinessUnitId,
  businessUnitName,
  search,
  onSearchChange,
}: {
  provider: WorkspaceProvider;
  organizationBusinessUnitId: string;
  businessUnitName?: string;
  search: ProviderPageSearch;
  onSearchChange: (changes: Partial<ProviderPageSearch>) => void;
}) {
  const [starting, setStarting] = useState(false);
  const canViewCredentials = provider.capabilities.canViewCredentials;
  const verificationEnabled =
    provider.primarySourceVerificationEnabled && canViewCredentials;
  const scope = useMemo(
    () => ({
      organizationBusinessUnitId,
      providerUserId: provider.providerUserId,
      expectedAffiliationId: provider.affiliationId,
    }),
    [
      organizationBusinessUnitId,
      provider.providerUserId,
      provider.affiliationId,
    ],
  );
  const verification = useProviderVerification({
    scope,
    runId: search.run,
    enabled: verificationEnabled,
  });
  const hasDocumentsTab = provider.credentialModel !== "legacy";
  const tab = resolveTab({
    requested: search.tab,
    verificationEnabled,
    hasDocumentsTab,
  });
  const recordView = RECORD_VIEWS[tab];
  const data = verification.report.data;
  const identity = getReportIdentity({
    providerName: provider.name,
    identity: data?.identity,
  });
  const runFacts: ProviderRunFacts | undefined = verificationEnabled
    ? {
        npi: identity.npi ?? null,
        additionalNames: identity.additionalNames,
        licensedStates: licensedStates(data?.items ?? []),
      }
    : undefined;
  const canStart = provider.capabilities.canEditCredentials;
  const history = verification.history;
  const runDisabled =
    verification.hasActiveRun || history.isPending || history.isError;
  // The roster status is known before run history loads, so the label does
  // not change once the history arrives.
  const hasRun =
    verification.runs.length > 0 ||
    (provider.psvStatus?.status ?? PsvProviderStatus.NotRun) !==
      PsvProviderStatus.NotRun;

  return (
    <PageShell>
      <ProviderPageHeader
        provider={provider}
        organizationBusinessUnitId={organizationBusinessUnitId}
        businessUnitName={businessUnitName}
        tabLabel={TAB_LABELS[tab]}
        runFacts={runFacts}
        verificationStatus={
          verificationEnabled ? (provider.psvStatus ?? undefined) : undefined
        }
        runVerification={
          verificationEnabled && canStart
            ? {
                disabled: runDisabled,
                hasRun,
                onRun: () => setStarting(true),
              }
            : undefined
        }
      />
      {!canViewCredentials && (
        <p className="text-sm text-muted-foreground">{NO_CREDENTIAL_ACCESS}</p>
      )}
      {canViewCredentials && (
        <Tabs
          value={tab}
          onValueChange={(value) =>
            onSearchChange({ tab: value as ProviderPageTab })
          }
        >
          <TabsList className="h-auto w-full justify-start gap-2 rounded-none border-b bg-transparent p-0">
            {verificationEnabled && (
              <TabsTrigger
                value={ProviderPageTab.Verification}
                className={TAB_TRIGGER_CLASS}
              >
                {TAB_LABELS[ProviderPageTab.Verification]}
              </TabsTrigger>
            )}
            <TabsTrigger
              value={ProviderPageTab.Credentials}
              className={TAB_TRIGGER_CLASS}
            >
              {TAB_LABELS[ProviderPageTab.Credentials]}
            </TabsTrigger>
            {hasDocumentsTab && (
              <TabsTrigger
                value={ProviderPageTab.Documents}
                className={TAB_TRIGGER_CLASS}
              >
                {TAB_LABELS[ProviderPageTab.Documents]}
              </TabsTrigger>
            )}
          </TabsList>
          <TabsContent value={ProviderPageTab.Verification} className="mt-4">
            {/* A new run (picked from the history, or replacing the latest)
                starts a fresh review, so an open confirmation can never
                apply to a different run's item. */}
            <VerificationTab
              key={verification.selectedRunId}
              provider={provider}
              scope={scope}
              verification={verification}
              search={search}
              onSearchChange={onSearchChange}
              onRunVerification={() => setStarting(true)}
            />
          </TabsContent>
          {/* One panel serves both record tabs and stays mounted, so moving
              between Credentials, Documents and Verification keeps uploads
              and drafts. */}
          <TabsContent
            value={recordView ? tab : ProviderPageTab.Credentials}
            forceMount
            hidden={!recordView}
            className="mt-4"
          >
            <CredentialRecord
              provider={provider}
              organizationBusinessUnitId={organizationBusinessUnitId}
              view={recordView ?? CredentialVaultTab.Credentials}
            />
          </TabsContent>
        </Tabs>
      )}
      {starting && (
        <ProviderStartVerification
          provider={provider}
          organizationBusinessUnitId={organizationBusinessUnitId}
          onClose={() => setStarting(false)}
          onRestoreFocus={() => {}}
        />
      )}
    </PageShell>
  );
}
